import React, { useEffect, useState } from "react";
import { Box, Stack, Button, Grid, Typography } from "@mui/material";
import RSelect from "react-select";
import {
  getCampaignBillboardsAPI,
  getlinegraphDataAPI,
  getBoxPlotDataAPI,
  getDayOfWeekBoxPlotDataAPI,
  getMonthlyHeatmapAPI,
  getDailyLineGraphAPI,
} from "../../apis/campaigns.apis";
import Loader from "../../components/Loader";
import SpeedDurationBoxPlot from "./SpeedDurationBoxPlot";
import HourlyVisibilityLineGraph from "./HourlyVisibilityLineGraph";
import DayOfWeekBoxPlot from "./DayOfWeekBoxPlot";
import MonthlyHeatmapPlot from "./MonthlyHeatmapPlot";
import DailyLineGraph from "./DailyLineGraph";
import { toast } from "react-toastify";

export default function CampaignLocationFilter({ campaignId }) {
  const [zones, setZones] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [locations, setLocations] = useState([]);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selected, setSelected] = useState({
    zone: null,
    state: null,
    city: null,
    location: null,
  });
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const currentYear = new Date().getFullYear();
  const [selectedGraph, setSelectedGraph] = React.useState(null); // e.g. 'boxplot', 'visibility', 'dayOfWeek', etc.
  const [graphData, setGraphData] = React.useState(null);

  const handleMonthChange = async (month) => {
    setSelectedMonth(month);
    
  }; 
  useEffect(() => {
    const fetchBillboards = async () => {
      try {
        const res = await getCampaignBillboardsAPI(campaignId);
        const result = res.data;
        setData(result);
        const zoneOptions = result.map((z) => ({
          label: z.zone_name,
          value: z.zone_id,
        }));
        setZones(zoneOptions);
      } catch (err) {
        console.error("Error fetching billboards:", err);
      }
    };
    fetchBillboards();
  }, [campaignId]);

  const graphConfigs = {
    boxplot: {
      api: getBoxPlotDataAPI,
      successMsg: "Box Plot Graph plotted successfully!",
      errorMsg: "No data available to plot box chart!",
    },
    visibility: {
      api: getlinegraphDataAPI,
      successMsg: "Visibility Graph plotted successfully!",
      errorMsg: "No data available to plot the graph!",
    },
    dayOfWeek: {
      api: getDayOfWeekBoxPlotDataAPI,
      successMsg: "Day of Week Graph plotted successfully!",
      errorMsg: "No data available to plot Day of Week Graph!",
    },
    monthlyHeatmap: {
      api: getMonthlyHeatmapAPI,
      successMsg: "Monthly Heatmap Graph plotted successfully!",
      errorMsg: "No data available for Monthly Heatmap!",
      payloadExtra: () => ({ month: selectedMonth, year: currentYear }),
    },
    dailyLine: {
      api: getDailyLineGraphAPI,
      successMsg: "Daily Line Graph plotted successfully!",
      errorMsg: "No data available for Daily Line Graph!",
    },
  };

  const handleShowGraph = async (graphType) => {
    const config = graphConfigs[graphType];
    if (!config) return;

    setIsLoading(true);
    setSelectedGraph(null);
    setGraphData(null);

    try {
      const payload = {
        campaign_id: campaignId,
        zone_id: selected.zone?.value || null,
        state_id: selected.state?.value || null,
        city_id: selected.city?.value || null,
        location: selected.location?.value || null,
        ...(config.payloadExtra ? config.payloadExtra() : {}),
      };

      const res = await config.api(payload);
      setSelectedGraph(graphType);
      if (res.success && res.data && Object.keys(res.data).length > 0) {
        setGraphData(res.data);
        
        toast.success(config.successMsg);
      } else {
        toast.error(config.errorMsg);
      }
    } catch {
      toast.error(`Failed to plot ${graphType} graph!`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (key, value) => {
    let newSelected = { ...selected, [key]: value };

    if (key === "zone") {
      const zone = data.find((z) => z.zone_id === value.value);
      const stateOptions =
        zone?.states.map((s) => ({
          label: s.state_name,
          value: s.state_id,
        })) || [];
      setStates(stateOptions);
      setCities([]);
      setLocations([]);
      newSelected = { zone: value, state: null, city: null, location: null };
    } else if (key === "state") {
      const zone = data.find((z) => z.zone_id === selected.zone?.value);
      const state = zone?.states.find((s) => s.state_id === value.value);
      const cityOptions =
        state?.cities.map((c) => ({
          label: c.city_name,
          value: c.city_id,
        })) || [];
      setCities(cityOptions);
      setLocations([]);
      newSelected = { ...selected, state: value, city: null, location: null };
    } else if (key === "city") {
      const zone = data.find((z) => z.zone_id === selected.zone?.value);
      const state = zone?.states.find(
        (s) => s.state_id === selected.state?.value
      );
      const city = state?.cities.find((c) => c.city_id === value.value);
      const locationOptions =
        city?.locations.map((l) => ({
          label: l,
          value: l,
        })) || [];
      setLocations(locationOptions);
      newSelected = { ...selected, city: value, location: null };
    } else if (key === "location") {
      newSelected = { ...selected, location: value };
    }

    setSelected(newSelected);
  };

  

  return (
    <Stack spacing={2}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={3}>
          <RSelect
            placeholder="Zone"
            options={zones}
            value={selected.zone}
            onChange={(v) => handleChange("zone", v)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <RSelect
            placeholder="State"
            options={
              states.length
                ? states
                : [{ label: "Select Zone First", value: 0 }]
            }
            value={selected.state}
            onChange={(v) => handleChange("state", v)}
            isDisabled={!selected.zone}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <RSelect
            placeholder="City"
            options={
              cities.length
                ? cities
                : [{ label: "Select State First", value: 0 }]
            }
            value={selected.city}
            onChange={(v) => handleChange("city", v)}
            isDisabled={!selected.state}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <RSelect
            placeholder="Location"
            options={
              locations.length
                ? locations
                : [{ label: "Select City First", value: 0 }]
            }
            value={selected.location}
            onChange={(v) => handleChange("location", v)}
            isDisabled={!selected.city}
          />
        </Grid>
      </Grid>

      <Box
        display="flex"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
      >
        <Button
          variant="contained"
          onClick={() => handleShowGraph("boxplot")}
          disabled={!selected.zone}
        >
          Show Box Plot Graphs
        </Button>
        <Button
          variant="contained"
          onClick={() => handleShowGraph("visibility")}
          disabled={!selected.zone}
        >
          Show Hourly Visibility Graph
        </Button>
        <Button
          variant="contained"
          onClick={() => handleShowGraph("dayOfWeek")}
          disabled={!selected.zone}
        >
          Show Day of Week Graph
        </Button>
        <Button
          variant="contained"
          onClick={() => handleShowGraph("monthlyHeatmap")}
          disabled={!selected.zone}
        >
          Show Monthly Heatmap
        </Button>
        <Button
          variant="contained"
          onClick={() => handleShowGraph("dailyLine")}
          disabled={!selected.zone}
        >
          Show Daily Line Graph
        </Button>
      </Box>

      {isLoading && <Loader open={true} />}

      {selectedGraph === "boxplot" && graphData && (
        <SpeedDurationBoxPlot data={graphData} />
      )}

      {selectedGraph === "visibility" && graphData && (
        <HourlyVisibilityLineGraph data={graphData} />
      )}

      {selectedGraph === "dayOfWeek" && graphData && (
        <DayOfWeekBoxPlot data={graphData} />
      )}

      {selectedGraph === "monthlyHeatmap" && (
        <MonthlyHeatmapPlot
          data={graphData || {}}
          selectedMonth={selectedMonth}
          onMonthChange={handleMonthChange}
        />
      )}

      {selectedGraph === "dailyLine" && graphData && (
        <DailyLineGraph data={graphData} />
      )}
    </Stack>
  );
}
